import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import { Button } from "../components/atoms";
import { fetchEmploymentTypes } from "../api/employmentTypes";
import {
    EMPTY_EMPLOYMENT_TYPE_FORM_STATE,
    type EmploymentTypeFormOutletContext,
    type EmploymentTypeFormState,
} from "../components/EmploymentTypeEditLayout";

/**
 * フォームフィールドラッパー
 */
const FormField = ({
    label,
    required = false,
    children,
}: {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}) => (
    <div className="flex items-start gap-6">
        <label className="w-28 text-sm text-gray-700 pt-2 flex-shrink-0">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex-1">{children}</div>
    </div>
);

/**
 * 雇用形態登録・編集ページ（基本情報タブ）
 */
const EmploymentTypeFormPage = () => {
    const navigate = useNavigate();
    const {
        setPageError,
        invalidFields,
        pendingFormState,
        setPendingFormState,
    } = useOutletContext<EmploymentTypeFormOutletContext>();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const employmentTypeId = id ? parseInt(id, 10) : null;

    // 編集モードで pendingFormState が無い場合（初回 or 従業員切替）のみ DB 取得
    const needsFetch = isEdit && employmentTypeId !== null && pendingFormState === null;
    const [loading, setLoading] = useState(needsFetch);

    const [formData, setFormData] = useState<{ code: string; name: string }>({
        code: pendingFormState?.code ?? "",
        name: pendingFormState?.name ?? "",
    });

    // 編集時にデータを取得（pendingFormState 未設定時だけ）
    useEffect(() => {
        if (!needsFetch || employmentTypeId === null) return;
        const loadData = async () => {
            setLoading(true);
            setPageError(null);
            try {
                const data = await fetchEmploymentTypes();
                const item = data.items.find((et) => et.id === employmentTypeId);
                if (!item) {
                    setPageError("雇用形態が見つかりません");
                    return;
                }
                const next: EmploymentTypeFormState = {
                    ...EMPTY_EMPLOYMENT_TYPE_FORM_STATE,
                    code: item.code,
                    name: item.name,
                };
                setFormData({ code: next.code, name: next.name });
                setPendingFormState(next);
            } catch (err) {
                console.error("Failed to fetch employment type:", err);
                setPageError(err instanceof Error ? err.message : "データの取得に失敗しました");
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [needsFetch, employmentTypeId, setPageError, setPendingFormState]);

    const handleInputChange = useCallback(
        (field: "code" | "name", value: string) => {
            setFormData((prev) => {
                const updated = { ...prev, [field]: value };
                // レイアウトの pendingFormState と同期
                setPendingFormState({
                    ...EMPTY_EMPLOYMENT_TYPE_FORM_STATE,
                    ...(pendingFormState ?? {}),
                    code: updated.code,
                    name: updated.name,
                });
                return updated;
            });
        },
        [pendingFormState, setPendingFormState],
    );

    const isFieldInvalid = (key: keyof EmploymentTypeFormState): boolean => {
        if (!invalidFields.includes(key)) return false;
        const value = formData[key as "code" | "name"];
        return !value || !value.trim();
    };

    const handleCancel = () => {
        navigate("/tenant/employment-type");
    };

    const handleNext = () => {
        const path = isEdit
            ? `/tenant/employment-type/${id}/work-rules`
            : "/tenant/employment-type/new/work-rules";
        navigate(path);
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <span className="text-gray-500">読み込み中...</span>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col bg-white rounded-lg overflow-hidden">
            <div className="flex-1 overflow-auto p-6">
                <div className="space-y-6">
                    <div className="text-xs text-red-500">「*」は登録必須項目です。</div>

                    {/* 雇用形態コード */}
                    <FormField label="雇用形態コード" required>
                        <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => handleInputChange("code", e.target.value)}
                            placeholder="001"
                            className={clsx("form-input-field w-60", isFieldInvalid("code") && "border-red-500")}
                        />
                    </FormField>

                    {/* 雇用形態名 */}
                    <FormField label="雇用形態名" required>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            placeholder="正社員"
                            className={clsx("form-input-field w-60", isFieldInvalid("name") && "border-red-500")}
                        />
                    </FormField>
                </div>
            </div>

            {/* フッターボタン */}
            <div className="flex justify-end gap-4 px-6 py-4">
                <Button variant="outline" size="md" onClick={handleCancel} className="px-8">
                    キャンセル
                </Button>
                <Button variant="primary" size="md" onClick={handleNext} className="px-8">
                    2.契約情報の設定へ進む
                </Button>
            </div>
        </div>
    );
};

export default EmploymentTypeFormPage;
